import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import { getClassNames, formatDate } from '../../../../utils/util';

import Image from '../../image/Image';
import ImageCarousel from '../../image-carousel/ImageCarousel';

import './product-listing.scss';

const ProductListing = ({ listing, actions, mini }) => {
  const {
    status,
    creationDate,
    classification,
    category,
    specification,
    brandName,
    productUnit,
    pricePerProductUnit,
    totalAmount,
    minimumOrderQuantity,
    offerAvailableFrom,
    offerExpiresOn,
    country,
    city,
    linkedMediaExternalReferences,
    additionalInformation,
    currencyCode,
  } = listing;

  const [open, setOpen] = useState(false);
  const [startCloseAnimation, setStartCloseAnimation] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(undefined);

  const detailsRef = useRef(null);

  const handleOverviewClick = () => {
    let timeout;

    if (open) {
      setStartCloseAnimation(true);

      timeout = setTimeout(() => setOpen(false), 300);
    } else {
      setOpen(true);
      setStartCloseAnimation(false);
    }

    return () => clearTimeout(timeout);
  };

  useEffect(() => {
    if (open && !mini) {
      setTimeout(() => detailsRef.current.scrollIntoView({ behavior: 'smooth' }), 200);
    }
  }, [open, mini]);

  const renderOverviewContent = () => {
    if (mini) {
      return (
        <>
          <p>{brandName ? brandName : specification.name}</p>
          <p>
            {parseFloat(pricePerProductUnit).toFixed(2)}/{productUnit}
          </p>
          <p>{formatDate(offerAvailableFrom)}</p>
        </>
      );
    }

    return (
      <>
        {linkedMediaExternalReferences && linkedMediaExternalReferences[0] && (
          <Image
            className="product-listing__overview__image"
            externalReference={linkedMediaExternalReferences[0]}
            alt={brandName ? brandName : specification.name}
          />
        )}
        {(!linkedMediaExternalReferences || !linkedMediaExternalReferences[0]) && (
          <div className="product-listing__no-image">
            <p>No Image </p>
          </div>
        )}
        <div>
          <p>
            <b>{brandName ? brandName : specification.name} -</b>{' '}
            {currencyCode + ' ' + parseFloat(pricePerProductUnit).toFixed(2)}/{productUnit}
          </p>
          <p>{additionalInformation}</p>
        </div>
      </>
    );
  };

  const renderDetailsContent = () => {
    if (mini) {
      return (
        <div className="product-listing__details__grid product-listing__details__grid--mini">
          <div className="product-listing__details__attribute product-listing__details__attribute--mini">
            <p>Total Amount of Product:</p>
            <p>{totalAmount}</p>
          </div>
          <div className="product-listing__details__attribute product-listing__details__attribute--mini">
            <p>Minimum Order Quantity:</p>
            <p>{minimumOrderQuantity}</p>
          </div>
          <div className="product-listing__details__attribute product-listing__details__attribute--mini">
            <p>Location:</p>
            <p>
              {city}, {country}
            </p>
          </div>
          <div className="product-listing__details__actions product-listing__details__actions--mini">{actions}</div>
        </div>
      );
    }

    return (
      <>
        <div className="product-listing__details__grid">
          <div className="product-listing__details__classification">
            <p>{classification.name}</p>
            <div>
              <p>{category.name}</p>
              <div>
                <p>{specification.name}</p>
                <div>
                  <p>{brandName}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="product-listing__details__status">
            <div>
              <p>Status</p>
              <p>{status}</p>
            </div>
            <div>
              <p>Creation Date</p>
              <p>{formatDate(creationDate)}</p>
            </div>
          </div>

          <div className="product-listing__details__attribute">
            <p>Product Unit:</p>
            <p>{productUnit}</p>
          </div>

          <div className="product-listing__details__attribute">
            <p>Price per Product Unit:</p>
            <p>{currencyCode + ' ' + parseFloat(pricePerProductUnit).toFixed(2)}</p>
          </div>

          <div className="product-listing__details__attribute">
            <p>Total Amount of Product Available:</p>
            <p>{totalAmount}</p>
          </div>

          <div className="product-listing__details__attribute">
            <p>Minimum Order Quantity:</p>
            <p>{minimumOrderQuantity}</p>
          </div>

          <div className="product-listing__details__attribute">
            <p>Offer available from:</p>
            <p>{formatDate(offerAvailableFrom)}</p>
          </div>

          <div className="product-listing__details__attribute">
            <p>Offer expires on:</p> <p>{formatDate(offerExpiresOn)}</p>
          </div>

          <div className="product-listing__details__attribute">
            <p>Location City:</p>
            <p>{city}</p>
          </div>

          <div className="product-listing__details__attribute">
            <p>Location Country:</p>
            <p>{country}</p>
          </div>
        </div>

        {linkedMediaExternalReferences && linkedMediaExternalReferences.length > 0 && (
          <div className="product-listing__details__attribute">
            <p>Photos</p>
            <div className="product-listing__details__photos">
              {linkedMediaExternalReferences.map((externalReference, index) => (
                <Image
                  key={index}
                  onClick={() => setSelectedImageIndex(index)}
                  className="product-listing__details__photos__photo"
                  externalReference={externalReference}
                  alt=""
                />
              ))}
            </div>
          </div>
        )}

        {additionalInformation && (
          <div className="product-listing__details__additional-info">
            <p>Additional Information</p>
            <p>{additionalInformation}</p>
          </div>
        )}

        <div className="product-listing__details__actions">{actions}</div>
      </>
    );
  };

  return (
    <>
      <section className={getClassNames('product-listing', { mini })} ref={detailsRef}>
        <section
          className={getClassNames('product-listing__overview', { mini })}
          onClick={handleOverviewClick}
          data-testid="product-listing-overview"
        >
          {renderOverviewContent()}
          <span className={getClassNames('product-listing__overview__icon-container', { mini })}>
            <div className={getClassNames('product-listing__overview__icon', { mini })} />
            <div className={getClassNames('product-listing__overview__icon', { mini, open: !startCloseAnimation })} />
          </span>
        </section>

        {open && (
          <section className={getClassNames('product-listing__details', { mini, close: startCloseAnimation })}>
            {renderDetailsContent()}
          </section>
        )}
      </section>

      {selectedImageIndex !== undefined && (
        <ImageCarousel
          images={linkedMediaExternalReferences}
          startIndex={selectedImageIndex}
          setStartIndex={setSelectedImageIndex}
        />
      )}
    </>
  );
};

ProductListing.propTypes = {
  listing: PropTypes.shape({}).isRequired,
  actions: PropTypes.node,
  mini: PropTypes.bool,
};

ProductListing.defaultProps = {
  actions: null,
  mini: true,
};

export default ProductListing;
